import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import { useLoggedInUser, useFeatureFlags, useProductAssetsByUser, useUser } from '@tcc/shared-data-lib';
import { initializeAnalytics } from '@tcc/analytics';
import { useBreakpoint } from '@tcc/fds';
import { ReportsPluginContainer } from '../containers/ReportsPluginContainer';
import { ConnectPluginContainer } from '../containers/ConnectPluginContainer';
import { DmbPluginContainer } from '../containers/DmbPluginContainer';
import { VariableRatePluginContainer } from '../containers/VariableRateContainer';
import { CropPlanningPluginContainer } from '../containers/CropPlanningPluginContainer';
import { ProgramsPluginContainer } from '../containers/ProgramsPluginContainer';
import { DashboardPluginContainer } from '../containers/DashboardPluginContainer';
import { InfoArchitecturePluginContainer } from '../containers/InfoArchitecturePluginContainer';
import { AskFieldviewWebPluginContainer } from '../containers/AskFieldviewWebPluginContainer';
import { FarmerNav } from '../components/FarmerNav/FarmerNav';
import { UnverifiedBlocker } from '../components/UnverifiedBlocker';
import { useOperations } from '../hooks/useOperations/useOperations';
import { useHistoryListener } from '../hooks/useHistoryListener';
import { FieldSelectionPluginContainer } from '../containers/FieldSelectionContainer';
import { SpinnerLoading } from '../components/SpinnerLoading/SpinnerLoading';
import { getUserData, reAuthenticate } from '../utils/wam';
import { ErrorFallback } from '../components/ErrorFallback';
import { DataInboxPluginContainer } from '../containers/DataInboxPluginContainer';
import { FixitToolPluginContainer } from '../containers/FixitToolPluginContainer';
import { DataHubPluginContainer } from '../containers/DataHubPluginContainer';

import styles from './styles.css';
import { AuthorizedRoute } from './AuthorizedRoute';
import { ROUTE } from '../constants/routes';
import { useFeatureFlagsConfigs, useProductAssetsByUserConfigs, useUserLoggedInConfigs } from '../utils/sharedData';
import { getAvailableFeatures } from '../utils/getAvailableFeatures';
import { ScriptCreatorPluginContainer } from '../containers/ScriptCreatorContainer';
import { queryParam } from '../utils/queryString';
import { ProxyBanner } from '../components/ProxyBanner/ProxyBanner';
import { SettingsPluginContainer } from '../containers/SettingsPluginContainer/SettingsPluginContainer';
import { useMarketingNotifications } from '../hooks/useMarketingNotifications';
import { useCobrowsingSupport } from '../hooks/useCobrowsingSupport';
import { InSeasonPluginContainer } from '../containers/InSeasonContainer';

export const ApplicationView = () => {
  const { clientId } = getUserData();
  const location = useLocation();
  const {
    operations,
    operationsSelected,
    setSelectedOperations,
  } = useOperations(clientId);

  const { isDesktop } = useBreakpoint();

  const [expanded, setExpandedState] = useState(isDesktop);

  const { user } = useLoggedInUser(useUserLoggedInConfigs);
  const impersonatedUserId = queryParam('client-id');
  const userId = impersonatedUserId || user.id.toString();
  const { user: impersonatedUser } = useUser({
    config: { skip: !impersonatedUserId || user.id.toString() === impersonatedUserId },
    params: { legacyUserId: userId },
  });
  const { featureFlags, isLoading: isLoadingFeatureFlags } = useFeatureFlags(useFeatureFlagsConfigs(user));
  const { data: assets, isLoading: isLoadingAssets } = useProductAssetsByUser(useProductAssetsByUserConfigs(user));

  useEffect(() => {
    if (user && user.details) {
      initializeAnalytics({ userDetails: user.details, featureFlags });
    }
  }, [featureFlags, user]);

  // TODO: remove useHistoryListener prop after FEWEB-1305
  useHistoryListener(featureFlags?.['web.analytics.monitoring']);
  useMarketingNotifications();
  useCobrowsingSupport();

  const operationsSelectedIds = useMemo(() => operationsSelected.map((op) => op.operationId), [operationsSelected]);

  const blockUser = user?.details?.emailVerificationStatus === 'UNVERIFIED';
  if (blockUser) {
    return <UnverifiedBlocker onLogout={reAuthenticate} timeout={5000} />;
  }

  if (isLoadingAssets || isLoadingFeatureFlags) {
    return <SpinnerLoading />;
  }

  const availableFeatures = getAvailableFeatures({ user, assets, featureFlags, impersonatedUser });
  const Home = availableFeatures.dashboard ? ROUTE.Dashboard : ROUTE.Home;

  const includeSideNav = availableFeatures.informationArchitecture;

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <main className={`${styles.mainContainer} ${includeSideNav && styles.mainContainerRow}`}>
        {!includeSideNav && <FarmerNav
          operations={operations}
          operationsSelected={operationsSelected}
          setSelectedOperations={setSelectedOperations}
        />}
        {includeSideNav && <ProxyBanner />}
        {includeSideNav &&
          <InfoArchitecturePluginContainer
            expanded={expanded}
            operations={operations}
            operationsSelected={operationsSelected}
            setExpandedState={setExpandedState}
            setSelectedOperations={setSelectedOperations}
          />}
        <div className={styles.pluginWrapper}>
          <div className={styles.pluginContainer}>
            <Suspense fallback={<SpinnerLoading />}>
              <Switch>
                <AuthorizedRoute
                  isEnabled={availableFeatures.reports}
                  path={ROUTE.Reports}
                  render={() => (
                    <ReportsPluginContainer operationsSelected={operationsSelected} />
                  )}
                />
                <Route path={ROUTE.DataInbox} render={() => <DataInboxPluginContainer operationsSelected={operationsSelected} />} />
                <Route path={ROUTE.FixitTool} render={() => <FixitToolPluginContainer operationsSelected={operationsSelected} />} />
                <Route
                  path={ROUTE.Dmb}
                  render={() => (
                    <DmbPluginContainer
                      operations={operations}
                      operationsSelected={operationsSelectedIds}
                    />
                  )} />
                <Route
                  path={ROUTE.VariableRate}
                  render={() => (
                    <VariableRatePluginContainer
                      operations={operations}
                      operationsSelected={operationsSelectedIds}
                    />
                  )} />
                <Route
                  path={ROUTE.FieldSelection}
                  render={() => (
                    <section className={styles.flexFSWrapper}>
                      <FieldSelectionPluginContainer
                        operationsSelected={operationsSelected}
                      />
                    </section>
                  )}
                />
                <Route path={ROUTE.ScriptCreator} render={() => (
                  <section>
                    <ScriptCreatorPluginContainer
                      operations={operations}
                      operationsSelected={operationsSelectedIds}
                    />
                  </section>
                )} />
                <Route path={ROUTE.Settings} render={() => (
                  <SettingsPluginContainer
                    operations={operations}
                    operationsSelected={operationsSelectedIds}
                  />
                )} />
                <AuthorizedRoute
                  isEnabled={availableFeatures.programs}
                  path={ROUTE.Programs}
                  component={ProgramsPluginContainer}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.dataHub}
                  path={ROUTE.DataHub}
                  render={() => (
                    <DataHubPluginContainer operationsSelected={operationsSelected} />
                  )}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.dashboard}
                  path={ROUTE.Dashboard}
                  render={() => (
                    <DashboardPluginContainer operationsSelected={operationsSelected} />
                  )}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.connect}
                  path={ROUTE.Partners}
                  component={ConnectPluginContainer}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.cropPlanning}
                  path={ROUTE.CropPlanning}
                  component={CropPlanningPluginContainer}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.askFieldView}
                  path={ROUTE.AskFieldView}
                  component={AskFieldviewWebPluginContainer}
                />
                <AuthorizedRoute
                  isEnabled={availableFeatures.inSeason}
                  path={ROUTE.InSeason}
                  component={InSeasonPluginContainer}
                />
                <Route path="*">
                  <Redirect to={{
                    ...location,
                    pathname: Home
                  }} />
                </Route>
              </Switch>
            </Suspense>
          </div>
        </div>
      </main>
    </ErrorBoundary>
  );
};
